'use strict';

import { flow, action, observable, configure, observe, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from '@uw-it-sis/lib-react/lib/AppStore';

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';
import {isEmpty} from '@uw-it-sis/lib-react/lib/Utils';
import {normalize} from './CourseUtils';

import CourseMessagesStore from './CourseMessagesStore';
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import bookmarksStore from './BookmarksStore';
import notifyStore from './NotifyStore';

import events from '@uw-it-sis/lib-react/lib/Events';
import BookmarkEvents from './BookmarkEvents';
import PlanEvents from './PlanEvents';
import NotifyEvents from './NotifyEvents';

/**
 * Course Details Store.
 *
 * This store has to be instantiated.
 */
class CourseDetailsStore {

    state = States.initial;

    /**
     * Construct a CourseDetailsStore.
     */
    constructor() {
        makeObservable(this, {
            state: observable,
            checkDataLoadStates: action
        });

        this.courseData = {};
        this.failedToAddMsg = "";

        // This is the object to track the status of the loads.
        this.dataLoadStates = {
            courseDetails: States.initial,
            courseMessages: States.initial,
            publishedTerms: States.initial,
            bookmarks: States.initial,
            notify: States.initial
        };
    }

    /**
     * Calls disposers and unregisters listeners.
     */
    unmount() {
        this.courseMessagesStoreDisposer();
        this.termStoreDisposer();
        this.bookmarksStoreDisposer();
        this.notifyStoreDisposer();

        events.remove(BookmarkEvents.deleted, (code) => this.onRemoveBookmark(code));
        events.remove(BookmarkEvents.added, (bookmark)  => this.onAddBookmark(bookmark));
        events.remove(NotifyEvents.deleted, (sectionId) => this.onRemoveSubscription(sectionId));
        events.remove(NotifyEvents.added, (sectionId)  => this.onAddSubscription(sectionId));
        events.remove(PlanEvents.planItemAdded, (eo) => this.onPlanItemAdded(eo));
        events.remove(PlanEvents.planItemRemoved, (eo) => this.onPlanItemRemoved(eo));
        events.remove(PlanEvents.planItemError, (eo) => this.onPlanItemError(eo));
    }

    unmountCourseDetailsOnly() {
        events.remove(PlanEvents.planItemAdded, (eo) => this.onPlanItemAdded(eo));
        events.remove(PlanEvents.planItemRemoved, (eo) => this.onPlanItemRemoved(eo));
        events.remove(PlanEvents.planItemError, (eo) => this.onPlanItemError(eo));
    }

    /**
     * (Re)initializes the component with new
     * @param courseCode The course code.
     * @param courseId The id of the specific version of the course.
     * @param sectionFilters Section filters to apply.
     */
    mount(courseCode, courseId, sectionFilters) {
        this.courseCode = normalize(courseCode);
        this.courseId = courseId;
        this.sectionFilters = sectionFilters;

        // Observe state changes to courseMessagesStore.state
        this.courseMessagesStore = new CourseMessagesStore(this.courseCode);

        this.courseMessagesStoreDisposer = observe(this.courseMessagesStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.courseMessages = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        this.termStoreDisposer = observe(termStore, "state", (change) => {
            this.dataLoadStates.publishedTerms = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        this.bookmarksStoreDisposer = observe(bookmarksStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.bookmarks = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        this.notifyStoreDisposer = observe(notifyStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.notify = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        // Listen for events
        events.listen(BookmarkEvents.deleted, (code) => this.onRemoveBookmark(code));
        events.listen(BookmarkEvents.added, (bookmark)  => this.onAddBookmark(bookmark));
        events.listen(NotifyEvents.deleted, (sectionId) => this.onRemoveSubscription(sectionId));
        events.listen(NotifyEvents.added, (sectionId)  => this.onAddSubscription(sectionId));
        events.listen(PlanEvents.planItemAdded, (eo) => this.onPlanItemAdded(eo));
        events.listen(PlanEvents.planItemRemoved, (eo) => this.onPlanItemRemoved(eo));
        events.listen(PlanEvents.planItemError, (eo) => this.onPlanItemError(eo));

        // Re-init the course messages store.
        this.courseMessagesStore.initialize(normalize(courseCode));

        this.courseData = {};

        this.failedToAddMsg = "";

        // This is the object to track the status of the loads.
        this.dataLoadStates = {
            courseDetails: States.initial,
            courseMessages: States.initial,
            publishedTerms: States.initial,
            bookmarks: States.initial
        };

        // Kick off the data load(s).
        this.loadData();
    }

    /**
     * (Re)initializes the component with only course details
     * @param courseCode The course code.
     * @param courseId The id of the specific version of the course.
     */
    mountCourseDetailsOnly(courseCode, courseId) {
        this.courseCode = normalize(courseCode);
        this.courseId = courseId;

        // Observe state changes to courseMessagesStore.state
        this.courseMessagesStore = new CourseMessagesStore(courseCode);

        // Listen for events
        events.listen(PlanEvents.planItemAdded, (eo) => this.onPlanItemAdded(eo));
        events.listen(PlanEvents.planItemRemoved, (eo) => this.onPlanItemRemoved(eo));
        events.listen(PlanEvents.planItemError, (eo) => this.onPlanItemError(eo));

        // Re-init the course messages store.
        this.courseMessagesStore.initialize(normalize(courseCode));

        this.courseData = {};

        this.failedToAddMsg = "";

        // Kick off the data load(s).
        this.loadData(true);
    }

    /**
     * Handler for bookmark remove events, so that course messages can be updated.
     */
    onRemoveBookmark(code) {
        if (appStore.isDebug()) {
            console.log(`CourseDetailsStore[${this.courseCode}].onRemoveBookmark: Got [${code}]`);
        }
        // If the removed bookmark was for this course then refresh the course messages.
        if (code === normalize(this.courseData.courseSummaryDetails.code)) {
            if (appStore.isDebug()) {
                console.log(`CourseDetailsStore[${this.courseCode}].onRemoveBookmark: Course codes matched. Reloading course messages.`);
            }
            this._loadCourseMessages();
        }
    }

    /**
     * Handler for bookmark add events, so that course messages can be updated.
     */
    onAddBookmark(bookmark) {
        if (appStore.isDebug()) {
            console.log(`CourseDetailsStore[${this.courseCode}].onAddBookmark: Got [${JSON.stringify(bookmark)}]`);
        }
        // If the removed bookmark was for this course then refresh the course messages.
        if (bookmark.code === normalize(this.courseData.courseSummaryDetails.code)) {
            if (appStore.isDebug()) {
                console.log(`CourseDetailsStore[${this.courseCode}].onAddBookmark: Course codes matched. Reloading course messages.`);
            }
            this._loadCourseMessages();
        }
    }

    /**
     * Handler for bookmark remove events, so that course messages can be updated.
     */
    onRemoveSubscription(sectionId) {
        if (appStore.isDebug()) {
            console.log(`CourseDetailsStore[${this.courseCode}].onRemoveSubscription: Got [${sectionId}].`);
        }
        this._loadNotifyData();
    }

    /**
     * Handler for bookmark add events, so that course messages can be updated.
     */
    onAddSubscription(sectionId) {
        if (appStore.isDebug()) {
            console.log(`CourseDetailsStore[${this.courseCode}].onAddSubscription: Got [${sectionId}].`);
        }
        this._loadNotifyData();
    }

    /**
     * Listener for plan item adds.
     * @param eventObject An event object with info related to the plan item that was added.
     */
    onPlanItemAdded(eventObject) {
        let code = "";
        if (!isEmpty(eventObject.code)) {
            code = normalize(eventObject.code);
        }

        if (appStore.isDebug()) {
            console.log(`CourseDetailsStore[${this.courseCode}].onPlanItemAdded: Got [${JSON.stringify(eventObject)}]`);
        }
        if (eventObject.isSection) {
            if (appStore.isDebug()) {
                console.log(`CourseDetailsStore[${this.courseCode}].onPlanItemAdded: Reloading data.`);
            }
            this.loadData(true);
            this._loadCourseMessages(); // need these too to display "Added...." in blue bar
        } else {
            if (this.courseCode === code) {
                if (appStore.isDebug()) {
                    console.log(`CourseDetailsStore[${this.courseCode}].onPlanItemAdded: Course codes matched.`);
                }
                this._loadCourseMessages();
            }
            else if (code === "") {
                if (appStore.isDebug()) {
                    console.log(`CourseDetailsStore[${this.courseCode}].onPlanItemAdded: Event data does not define one course code, assuming multiple item add.`);
                }
                this.loadData(true);
                this._loadCourseMessages();
            }
        }
    }

    /**
     * Listener for plan item errors.
     * @param eventObject An event object with info related to the plan item that errored out.
     */
    onPlanItemError(eventObject) {
        if (eventObject.isSection) {
            if (appStore.isDebug()) {
                console.log(`CourseDetailsStore[${this.courseCode}].onPlanItemError: Reloading data.`);
            }
            this.failedToAddMsg = eventObject.errMessage;
            this.loadData(true);
        }
    }

    /**
     * Listener for plan item removes.
     * @param eventObject The plan item that was added.
     */
    onPlanItemRemoved(eventObject) {
        if (appStore.isDebug()) {
            console.log(`CourseDetailsStore[${this.courseCode}].onPlanItemRemoved: Got [${JSON.stringify(eventObject)}]. Reloading data.`);
        }
        this.loadData(true);
    }

    /**
     * Load messages for this course, if authenticated.
     * @private
     */
    _loadCourseMessages() {
        // Load course messages if appropriate.
        if (appStore.isAuthenticated()) {
            if (appStore.isDebug()) {
                console.log(`CourseDetailsStore[${this.courseCode}]: Reloading course messages.`);
            }
            // Call for the reload.
            this.courseMessagesStore.loadCourseMessages(this.courseCode);
        } else {
            // Otherwise, just resolve the request.
            this.dataLoadStates.courseMessages = States.done;
        }
    }

    _loadBookmarks() {
        if (appStore.isAuthenticated()) {
            // Load bookmarks
            bookmarksStore.loadBookmarks();
        } else {
            this.dataLoadStates.bookmarks = States.done;
        }
    }

    /**
     * (Re)load notify data.
     * @private
     */
    _loadNotifyData() {
        if (! appStore.isAdviser() && appStore.isAuthenticated() && ! appStore.isSocialUser()) {
            if (appStore.isDebug()) {
                console.log(`CourseDetailsStore[${this.courseCode}]: Reloading notify data.`);
            }
            // Call for the reload.
            notifyStore.loadNotifyData(true);
        } else {
            // Otherwise, just resolve the request.
            this.dataLoadStates.notify = States.done;
        }
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    /**
     * Make a URL with request params and whatnot.
     * @param courseCode The course code
     * @param courseId The course id for the course version to load.
     * @param sectionFilters Section filters.
     * @private
     */
    _makeUrl(courseCode, courseId, sectionFilters) {
        let url = "api/courses/" + encodeURIComponent(courseCode) + "/details";
        if (courseId || sectionFilters) {
            url = url + "?";

            let appendAmpersand = false;

            if (courseId) {
                url = url + "courseId=" + encodeURIComponent(courseId);
                appendAmpersand = true;
            }

            if (sectionFilters) {
                if (appendAmpersand) {
                    url = url + "&";
                }
                url = url + "sectionFilters=" + encodeURIComponent(sectionFilters);
            }
        }
        return url;
    }

    /**
     * Initiate a data load.
     *
     * @param isCourseOnly Only reload the course data.
     */
    loadData = flow(function* (isLoadCourseOnly) {

        // Update the aggregate state to pending.
        this.state = States.pending;

        if (isLoadCourseOnly) {
            this.dataLoadStates.courseDetails = States.pending;
        } else {
            // Reset the data load states object.
            this.dataLoadStates = {
                courseDetails: States.pending,
                courseMessages: States.pending,
                publishedTerms: States.pending,
                bookmarks: States.pending,
                notify: States.pending
            };
            // Load published terms.
            termStore.load('publishedTerms');
            this._loadBookmarks();
            this._loadCourseMessages();
            this._loadNotifyData();
        }

        let url = this._makeUrl(this.courseCode, this.courseId, this.sectionFilters);

        try {
            this.courseData = yield appStore.getHttpClient().doGet(url, 'get course details');

            if (isLoadCourseOnly) {
                this.state = States.done;
            } else {
                this.dataLoadStates.courseDetails = States.done;
                // Precipitate a UI update if all of the data loads have completed.
                this.checkDataLoadStates();
            }
        } catch (error) {
            appStore.getExceptionUtils().handleException(error);
            this.state = States.error;
        }
    });

    clearFailedToAddMsg() {
        this.failedToAddMsg = "";
    }

    /**
     * Returns true if data is currently available on the data object. Otherwise, false.
     */
    hasCourseData() {
        return ! isEmpty(this.courseData);
    }

    /**
     * Returns the course details data.
     */
    getCourseDetails() {
        return this.courseData.courseSummaryDetails;
    }

    /**
     * Returns the course offerings.
     */
    getCourseOfferings(termId) {
        if (termId) {
            let courseOfferingInstitutions = this.courseData.courseOfferingInstitutionList.map(institution => {
                let courseOfferingTerms = {
                    "code": institution.code,
                    "name": institution.name,
                    "courseOfferingTermList": institution.courseOfferingTermList.filter(term => `${term.yearTerm.year}${term.yearTerm.term}` === termId)
                };
                // If there are no sections for a given term for this institution, return null instead of object
                return courseOfferingTerms.courseOfferingTermList.length > 0 ? courseOfferingTerms : null;
            });
            // Filter out institutions that are null, because they have no sections for the given term.
            // This only comes into play when multiple terms are published and an institution has sections in 1 of the terms but not others.
            return courseOfferingInstitutions.filter(institution => institution !== null);
        } else {
            return this.courseData.courseOfferingInstitutionList;
        }
    }

    /**
     * @returns the formatted string of the filters applied to the section results
     */
    getFiltersApplied() {
        return this.courseData.filtersApplied;
    }

    /**
     * Pass-thru to the course messages store.
     */
    getCourseMessages() {
        return this.courseMessagesStore.getCourseMessages();
    }
}

export default CourseDetailsStore;